import React, { useState } from 'react';
import { Button, message, Modal, Popconfirm, Skeleton, Spin, Tooltip, Typography, Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { File, FileText, Paperclip, Upload as UploadIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import type { Attachment } from '../../../api/KanbanTaskApiClient';
import { MAX_ATTACHMENT_MB, exceedsAttachmentLimit, formatAttachmentSize } from '../../../api/kanban/attachments';
import type { TaskStore } from '../../../stores/kanban/TaskStore';
import type { KanbanTask } from '../../../stores/KanbanTaskStores';

dayjs.extend(localizedFormat);

const { Dragger } = Upload;
const { Text } = Typography;

export type TaskAttachmentsProps = {
  task: KanbanTask;
  store: TaskStore;
  readOnly: boolean;
  /**
   * Anonymous (public share-link) board. Attachment delete is hidden here on purpose — same product
   * decision as the column menu's delete item and the bulk-delete buttons in ListView: deletion is off
   * the public board "for now", upload and everything else stay. Do not fold this into `readOnly`: a
   * link visitor is not read-only (see useReadOnly's decision-4 comment) and upload must keep working.
   */
  isAnonymous: boolean;
};

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'];

type ErrorBody = { code?: string; reason?: string };

function responseOf(error: unknown): { status?: number; data?: ErrorBody } | undefined {
  return (error as { response?: { status?: number; data?: ErrorBody } } | null | undefined)?.response;
}

function isConflict(error: unknown): boolean {
  return responseOf(error)?.status === 409;
}

function isImage(att: Attachment): boolean {
  const ext = att.fileName.split('.').pop()?.toLowerCase() || '';
  return att.resourceType === 'image' || IMAGE_EXTENSIONS.includes(ext);
}

function uploadedLabel(att: Attachment): string {
  if (!att.uploadedAt) return '';
  const d = dayjs(att.uploadedAt);
  return d.isValid() ? d.format('LLL') : '';
}

/**
 * Attachments block above the drawer's tabs. Rows come from the server's attachment index
 * (`Attachment`: fileName, sizeBytes, uploadedAt) — nothing is derived from the URL any more.
 *
 * A `409` from `store.uploadTaskAttachment` means the server already has a file with that name for
 * this task; the user is offered a replace, which retries the same upload with `confirmation=true`.
 * The size gate equals the server's `KanbanAttachmentRules.MaxBytes` (100 MB, every file type).
 */
const TaskAttachments: React.FC<TaskAttachmentsProps> = observer(({ task, store, readOnly, isAnonymous }) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const canDelete = !isAnonymous && !readOnly;

  const attachments = store.attachments;
  const loadingList = !!store.attachmentsLoading && attachments.length === 0;

  const attemptUpload = async (file: File, confirmation: boolean): Promise<boolean> => {
    try {
      const result = await store.uploadTaskAttachment(task.id, file, confirmation);
      if (result) {
        message.success(t('AttachmentUploaded', { name: file.name }));
        return true;
      }
      message.error('Upload failed');
      return false;
    } catch (error) {
      if (!confirmation && isConflict(error)) {
        return new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: t('FileAlreadyExists'),
            okText: t('Replace'),
            cancelText: t('Cancel'),
            onOk: async () => resolve(await attemptUpload(file, true)),
            onCancel: () => resolve(false),
          });
        });
      }
      console.error('Error uploading attachment:', error);
      const body = responseOf(error)?.data;
      if (body?.reason === 'TooLarge') {
        message.error(t('FileSizeLimit', { size: MAX_ATTACHMENT_MB }));
        return false;
      }
      const reason = body?.code || (error instanceof Error ? error.message : 'Unknown error');
      message.error(`Failed to upload: ${reason}`);
      return false;
    }
  };

  const customRequest: UploadProps['customRequest'] = async ({ file, onSuccess, onError }: any) => {
    setUploading(true);
    try {
      const ok = await attemptUpload(file as File, false);
      if (ok) {
        onSuccess?.({}, new XMLHttpRequest());
      } else {
        onError?.(new Error('Upload failed'));
      }
    } catch (error) {
      onError?.(error as Error);
    } finally {
      setUploading(false);
    }
  };

  const handleChange: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'error') {
      message.error(`${info.file.name} upload failed.`);
    }
    setFileList(info.fileList.slice(-5));
  };

  const handleDelete = async (publicId: string) => {
    setDeletingIds((prev) => new Set(prev).add(publicId));
    try {
      const success = await store.deleteAttachment(publicId, task.id);
      message[success ? 'success' : 'error'](t(success ? 'AttachmentDeleted' : 'FailedToDeleteAttachment'));
    } catch (error) {
      console.error(error);
      message.error(t('FailedToDeleteAttachment'));
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(publicId);
        return next;
      });
    }
  };

  return (
    <section className="task-attachments">
      <Text className="task-drawer__section-title">{t('Attachments')}</Text>

      <Spin spinning={uploading}>
        <div className="task-attachments__dropzone" style={readOnly ? { pointerEvents: 'none', opacity: 0.5 } : undefined}>
          <Dragger
            name="file"
            fileList={fileList}
            onChange={handleChange}
            customRequest={customRequest}
            showUploadList={{ showRemoveIcon: true }}
            multiple={false}
            beforeUpload={(file) => {
              if (readOnly) {
                message.error(t('YouHaveReadOnlyPermissions'));
                return Upload.LIST_IGNORE;
              }
              if (exceedsAttachmentLimit(file.size)) {
                message.error(t('FileSizeLimit', { size: MAX_ATTACHMENT_MB }));
                return Upload.LIST_IGNORE;
              }
              return true;
            }}
          >
            <p className="ant-upload-drag-icon">
              <UploadIcon size={36} />
            </p>
            <p className="ant-upload-text">{t('ClickOrDragFileToUpload')}</p>
            <p className="ant-upload-hint">{t('SupportForSingleOrBulkUpload')}</p>
          </Dragger>
        </div>
      </Spin>

      {attachments.length > 0 && (
        <div className="task-attachments__list">
          <Text strong>
            {t('AttachedFiles')} ({attachments.length})
          </Text>
          <div className="task-attachments__grid">
            {attachments.map((att) => {
              const Icon = isImage(att) ? File : FileText;
              const size = formatAttachmentSize(att.sizeBytes);
              const uploaded = uploadedLabel(att);
              const metaTitle = `${t('AttachmentSize')}: ${size}${uploaded ? ` · ${t('UploadedAt')}: ${uploaded}` : ''}`;
              return (
                <div key={att.publicId} className="task-attachments__card">
                  <Spin spinning={deletingIds.has(att.publicId)} size="small">
                    <div className="task-attachments__card-content">
                      <Icon size={24} className="task-attachments__icon" />
                      <div className="task-attachments__details">
                        <Tooltip title={att.fileName}>
                          <a href={att.url} target="_blank" rel="noopener noreferrer" className="task-attachments__filename">
                            {att.fileName.length > 20 ? `${att.fileName.slice(0, 17)}...` : att.fileName}
                          </a>
                        </Tooltip>
                        <Text type="secondary" className="task-attachments__meta" title={metaTitle}>
                          {size}
                          {uploaded ? ` · ${uploaded}` : ''}
                        </Text>
                        <div className="task-attachments__actions">
                          <Button type="text" size="small" onClick={() => window.open(att.url, '_blank')}>
                            {t('View')}
                          </Button>
                          {canDelete && (
                            <Popconfirm
                              title={t('ConfirmDeleteAttachment')}
                              description={t('AreYouSureDeleteAttachment')}
                              onConfirm={() => handleDelete(att.publicId)}
                              okText={t('Delete')}
                              cancelText={t('Cancel')}
                              okButtonProps={{ danger: true }}
                            >
                              <Button type="text" danger size="small">
                                {t('Delete')}
                              </Button>
                            </Popconfirm>
                          )}
                        </div>
                      </div>
                    </div>
                  </Spin>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loadingList && (
        <div className="task-attachments__list" aria-busy="true">
          <Text strong>{t('AttachedFiles')}</Text>
          <div className="task-attachments__grid">
            {[0, 1].map((i) => (
              <div key={i} className="task-attachments__card task-attachments__card--placeholder">
                <div className="task-attachments__card-content">
                  <Skeleton.Avatar active shape="square" size={24} />
                  <div className="task-attachments__details">
                    <Skeleton active title={{ width: '70%' }} paragraph={{ rows: 1, width: '40%' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loadingList && attachments.length === 0 && !uploading && (
        <div className="task-attachments__empty">
          <Paperclip size={24} />
          <Text type="secondary">{t('NoAttachmentsYet')}</Text>
        </div>
      )}
    </section>
  );
});

export default TaskAttachments;
